/**
 * Default family tree data per memorial ID.
 * Each node: { id, name, relation, birthYear, deathYear?, avatar, bio, isDeceased, generation }
 * Each link: { source, target, type }
 *
 * Supported link types: parent | spouse | sibling | child | cousin
 */

export const DEFAULT_FAMILY_TREES = {
  m1: {
    nodes: [
      {
        id: 'n1',  name: 'Eleanor Grace Thompson', relation: 'Deceased',
        birthYear: 1942, deathYear: 2023, isDeceased: true, generation: 0,
        avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=400&auto=format&fit=crop',
        bio: 'Beloved teacher and community pillar of Willow Creek for over 40 years.',
      },
      {
        id: 'n2',  name: 'David Thompson', relation: 'Husband',
        birthYear: 1940, isDeceased: false, generation: 0,
        avatar: 'https://images.unsplash.com/photo-1552058544-f2b08422138a?q=80&w=400&auto=format&fit=crop',
        bio: 'Devoted husband and retired carpenter.',
      },
      {
        id: 'n3',  name: 'Harold Grace', relation: 'Father',
        birthYear: 1915, deathYear: 1988, isDeceased: true, generation: -1,
        avatar: null, bio: 'A farmer and community leader.',
      },
      {
        id: 'n4',  name: 'Mabel Grace', relation: 'Mother',
        birthYear: 1918, deathYear: 2001, isDeceased: true, generation: -1,
        avatar: null, bio: 'A homemaker and gifted baker.',
      },
      {
        id: 'n5',  name: 'Robert Thompson Sr.', relation: 'Father-in-law',
        birthYear: 1912, deathYear: 1995, isDeceased: true, generation: -1,
        avatar: null, bio: 'A respected local judge.',
      },
      {
        id: 'n6',  name: 'Dorothy Thompson', relation: 'Mother-in-law',
        birthYear: 1916, deathYear: 2005, isDeceased: true, generation: -1,
        avatar: null, bio: 'A nurse and devoted mother.',
      },
      {
        id: 'n7',  name: 'James Thompson', relation: 'Son',
        birthYear: 1968, isDeceased: false, generation: 1,
        avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?q=80&w=400&auto=format&fit=crop',
        bio: 'Engineer living in Portland, OR.',
      },
      {
        id: 'n8',  name: 'Susan Thompson', relation: 'Daughter',
        birthYear: 1971, isDeceased: false, generation: 1,
        avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=400&auto=format&fit=crop',
        bio: "Teacher, following in her mother's footsteps.",
      },
      {
        id: 'n9',  name: 'Patricia Grace', relation: 'Sister',
        birthYear: 1945, isDeceased: false, generation: 0,
        avatar: null, bio: 'Retired librarian.',
      },
      {
        id: 'n10', name: 'Emily Thompson', relation: 'Granddaughter',
        birthYear: 1998, isDeceased: false, generation: 2,
        avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=400&auto=format&fit=crop',
        bio: 'Medical student and aspiring doctor.',
      },
      {
        id: 'n11', name: 'Lucas Thompson', relation: 'Grandson',
        birthYear: 2001, isDeceased: false, generation: 2,
        avatar: null, bio: 'High school student with a love for music.',
      },
      // ── Cousins ──────────────────────────────────────────────────────────
      {
        id: 'n12', name: 'Arthur Grace Jr.', relation: 'Cousin',
        birthYear: 1943, isDeceased: false, generation: 0,
        avatar: null,
        bio: "Son of Harold's brother Walter. Grew up alongside Eleanor on neighbouring farms.",
      },
      {
        id: 'n13', name: 'Ruth Grace-Holloway', relation: 'Cousin',
        birthYear: 1947, isDeceased: false, generation: 0,
        avatar: null,
        bio: "Patricia and Eleanor's cousin on the Grace side. Retired schoolteacher in Vermont.",
      },
      {
        id: 'n14', name: 'Oliver Holloway', relation: 'Cousin (once removed)',
        birthYear: 1972, isDeceased: false, generation: 1,
        avatar: null,
        bio: "Ruth's son. Software developer with a passion for genealogy research.",
      },
    ],
    links: [
      { source: 'n1',  target: 'n2',  type: 'spouse'  },
      { source: 'n3',  target: 'n1',  type: 'parent'  },
      { source: 'n4',  target: 'n1',  type: 'parent'  },
      { source: 'n5',  target: 'n2',  type: 'parent'  },
      { source: 'n6',  target: 'n2',  type: 'parent'  },
      { source: 'n1',  target: 'n7',  type: 'parent'  },
      { source: 'n1',  target: 'n8',  type: 'parent'  },
      { source: 'n1',  target: 'n9',  type: 'sibling' },
      { source: 'n7',  target: 'n10', type: 'parent'  },
      { source: 'n7',  target: 'n11', type: 'parent'  },
      // Cousin links
      { source: 'n1',  target: 'n12', type: 'cousin'  },
      { source: 'n1',  target: 'n13', type: 'cousin'  },
      { source: 'n9',  target: 'n13', type: 'cousin'  },
      { source: 'n13', target: 'n14', type: 'parent'  },
      { source: 'n12', target: 'n14', type: 'cousin'  },
    ],
  },

  m2: {
    nodes: [
      {
        id: 'n1', name: 'Marcus Aurelius Chen', relation: 'Deceased',
        birthYear: 1955, deathYear: 2022, isDeceased: true, generation: 0,
        avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=400&auto=format&fit=crop',
        bio: 'Decorated naval officer and restaurateur.',
      },
      {
        id: 'n2', name: 'Linda Chen', relation: 'Wife',
        birthYear: 1958, isDeceased: false, generation: 0,
        avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=400&auto=format&fit=crop',
        bio: 'Retired nurse and community volunteer.',
      },
      {
        id: 'n3', name: 'Wei Chen', relation: 'Father',
        birthYear: 1928, deathYear: 1999, isDeceased: true, generation: -1,
        avatar: null, bio: 'Immigrant entrepreneur who built a new life in San Francisco.',
      },
      {
        id: 'n4', name: 'Mei Chen', relation: 'Mother',
        birthYear: 1930, deathYear: 2010, isDeceased: true, generation: -1,
        avatar: null, bio: 'Devoted mother and skilled seamstress.',
      },
      {
        id: 'n5', name: 'Angela Chen', relation: 'Daughter',
        birthYear: 1982, isDeceased: false, generation: 1,
        avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=400&auto=format&fit=crop',
        bio: 'Attorney in San Francisco.',
      },
      {
        id: 'n6', name: 'Michael Chen', relation: 'Son',
        birthYear: 1985, isDeceased: false, generation: 1,
        avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?q=80&w=400&auto=format&fit=crop',
        bio: 'Now runs Harbor View Bistro.',
      },
      {
        id: 'n7', name: 'Thomas Chen', relation: 'Brother',
        birthYear: 1958, isDeceased: false, generation: 0,
        avatar: null, bio: 'Retired military officer.',
      },
      // ── Cousins ──────────────────────────────────────────────────────────
      {
        id: 'n8', name: 'David Wei-Chen', relation: 'Cousin',
        birthYear: 1953, isDeceased: false, generation: 0,
        avatar: null,
        bio: "Son of Wei's brother Liang. Restaurant owner in Hong Kong with close ties to the family.",
      },
      {
        id: 'n9', name: 'Fiona Chen-Park', relation: 'Cousin',
        birthYear: 1960, isDeceased: false, generation: 0,
        avatar: null,
        bio: "Marcus's first cousin on his father's side. Professor of East Asian Studies at UC Berkeley.",
      },
      {
        id: 'n10', name: 'Kevin Park', relation: 'Cousin (once removed)',
        birthYear: 1988, isDeceased: false, generation: 1,
        avatar: null,
        bio: "Fiona's son. Grew up visiting the Chen family restaurant every summer.",
      },
    ],
    links: [
      { source: 'n1', target: 'n2', type: 'spouse'  },
      { source: 'n3', target: 'n1', type: 'parent'  },
      { source: 'n4', target: 'n1', type: 'parent'  },
      { source: 'n1', target: 'n5', type: 'parent'  },
      { source: 'n1', target: 'n6', type: 'parent'  },
      { source: 'n1', target: 'n7', type: 'sibling' },
      // Cousin links
      { source: 'n1', target: 'n8', type: 'cousin'  },
      { source: 'n1', target: 'n9', type: 'cousin'  },
      { source: 'n7', target: 'n8', type: 'cousin'  },
      { source: 'n9', target: 'n10', type: 'parent' },
      { source: 'n1', target: 'n10', type: 'cousin' },
    ],
  },

  m3: {
    nodes: [
      {
        id: 'n1', name: 'Maya Lin Rodriguez', relation: 'Deceased',
        birthYear: 1978, deathYear: 2024, isDeceased: true, generation: 0,
        avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=400&auto=format&fit=crop',
        bio: 'Visionary muralist and community artist.',
      },
      {
        id: 'n2', name: 'Carlos Rodriguez', relation: 'Brother',
        birthYear: 1975, isDeceased: false, generation: 0,
        avatar: null, bio: 'Musician based in Brooklyn.',
      },
      {
        id: 'n3', name: 'Elena Rodriguez', relation: 'Mother',
        birthYear: 1950, isDeceased: false, generation: -1,
        avatar: null, bio: 'Colombian immigrant and poet.',
      },
      {
        id: 'n4', name: 'Jian Lin', relation: 'Father',
        birthYear: 1948, deathYear: 2015, isDeceased: true, generation: -1,
        avatar: null, bio: 'Chinese-American architect.',
      },
      {
        id: 'n5', name: 'Sofia Rodriguez', relation: 'Niece',
        birthYear: 2003, isDeceased: false, generation: 1,
        avatar: null, bio: 'Aspiring artist inspired by her aunt.',
      },
      // ── Cousins ──────────────────────────────────────────────────────────
      {
        id: 'n6', name: 'Isabella Rodriguez-Vega', relation: 'Cousin',
        birthYear: 1977, isDeceased: false, generation: 0,
        avatar: null,
        bio: "Daughter of Elena's sister Rosa. Close to Maya growing up; also a visual artist in Miami.",
      },
      {
        id: 'n7', name: 'Marco Vega', relation: 'Cousin',
        birthYear: 1980, isDeceased: false, generation: 0,
        avatar: null,
        bio: "Isabella's brother. Documentary filmmaker who collaborated with Maya on community art projects.",
      },
      {
        id: 'n8', name: 'Lucia Vega', relation: 'Cousin (once removed)',
        birthYear: 2005, isDeceased: false, generation: 1,
        avatar: null,
        bio: "Isabella's daughter. Grew up hearing stories of Maya's murals and is studying fine arts.",
      },
    ],
    links: [
      { source: 'n3', target: 'n1', type: 'parent'  },
      { source: 'n4', target: 'n1', type: 'parent'  },
      { source: 'n3', target: 'n2', type: 'parent'  },
      { source: 'n4', target: 'n2', type: 'parent'  },
      { source: 'n1', target: 'n2', type: 'sibling' },
      { source: 'n2', target: 'n5', type: 'parent'  },
      // Cousin links
      { source: 'n1', target: 'n6', type: 'cousin'  },
      { source: 'n1', target: 'n7', type: 'cousin'  },
      { source: 'n2', target: 'n6', type: 'cousin'  },
      { source: 'n2', target: 'n7', type: 'cousin'  },
      { source: 'n6', target: 'n7', type: 'sibling' },
      { source: 'n6', target: 'n8', type: 'parent'  },
      { source: 'n1', target: 'n8', type: 'cousin'  },
    ],
  },
};

export const getFamilyTree = (memorialId) =>
  DEFAULT_FAMILY_TREES[memorialId] ?? DEFAULT_FAMILY_TREES.m1;

export const RELATION_COLORS = {
  spouse:  '#C9A84C',  // gold
  parent:  '#1E3A5F',  // navy
  sibling: '#7C3AED',  // purple
  child:   '#059669',  // green
  cousin:  '#DB2777',  // pink / rose
};

// Dash patterns per link type (null = solid)
export const RELATION_DASH = {
  spouse:  '6 3',
  sibling: '4 4',
  cousin:  '8 4 2 4',  // distinctive long-short dash for cousins
  parent:  null,
  child:   null,
};

export const RELATION_LABELS = {
  spouse:  'Spouse / Partner',
  parent:  'Parent / Child',
  sibling: 'Sibling',
  child:   'Child',
  cousin:  'Cousin',
};

export const GENERATION_LABELS = {
  '-2': 'Great-Grandparents',
  '-1': 'Grandparents / Parents',
   '0': 'Core Generation',
   '1': 'Children / Cousins Once Removed',
   '2': 'Grandchildren',
};

/* ── Verification ─────────────────────────────────────────────────────────── */

export const DEFAULT_VERIFICATIONS = {
  m1: [
    { linkId: 'n1-n2-spouse',  sourceId: 'n1', targetId: 'n2',  type: 'spouse',  status: 'verified',   verifiedBy: 'James Thompson', verifiedAt: '2024-01-15T10:00:00Z', notes: 'Marriage certificate on file.' },
    { linkId: 'n3-n1-parent',  sourceId: 'n3', targetId: 'n1',  type: 'parent',  status: 'verified',   verifiedBy: 'Patricia Grace', verifiedAt: '2024-02-01T14:30:00Z', notes: 'Confirmed by family records.' },
    { linkId: 'n4-n1-parent',  sourceId: 'n4', targetId: 'n1',  type: 'parent',  status: 'verified',   verifiedBy: 'Patricia Grace', verifiedAt: '2024-02-01T14:30:00Z', notes: 'Confirmed by family records.' },
    { linkId: 'n5-n2-parent',  sourceId: 'n5', targetId: 'n2',  type: 'parent',  status: 'pending',    requestedBy: 'David Thompson', requestedAt: '2024-05-10T09:00:00Z', notes: 'Need to confirm with birth records.' },
    { linkId: 'n6-n2-parent',  sourceId: 'n6', targetId: 'n2',  type: 'parent',  status: 'unverified', notes: null },
    { linkId: 'n1-n7-parent',  sourceId: 'n1', targetId: 'n7',  type: 'parent',  status: 'verified',   verifiedBy: 'David Thompson', verifiedAt: '2024-01-20T11:00:00Z', notes: 'Birth certificate verified.' },
    { linkId: 'n1-n8-parent',  sourceId: 'n1', targetId: 'n8',  type: 'parent',  status: 'verified',   verifiedBy: 'David Thompson', verifiedAt: '2024-01-20T11:00:00Z', notes: 'Birth certificate verified.' },
    { linkId: 'n1-n9-sibling', sourceId: 'n1', targetId: 'n9',  type: 'sibling', status: 'pending',    requestedBy: 'Susan Thompson', requestedAt: '2024-06-01T16:00:00Z', notes: 'Shared parents confirmed by both parties.' },
    { linkId: 'n7-n10-parent', sourceId: 'n7', targetId: 'n10', type: 'parent',  status: 'unverified', notes: null },
    { linkId: 'n7-n11-parent', sourceId: 'n7', targetId: 'n11', type: 'parent',  status: 'unverified', notes: null },
    { linkId: 'n1-n12-cousin', sourceId: 'n1', targetId: 'n12', type: 'cousin',  status: 'unverified', notes: null },
    { linkId: 'n1-n13-cousin', sourceId: 'n1', targetId: 'n13', type: 'cousin',  status: 'unverified', notes: null },
    { linkId: 'n9-n13-cousin', sourceId: 'n9', targetId: 'n13', type: 'cousin',  status: 'pending',    requestedBy: 'Ruth Grace-Holloway', requestedAt: '2024-04-20T08:00:00Z', notes: 'Cousin connection through Grace family line.' },
    { linkId: 'n13-n14-parent',sourceId: 'n13',targetId: 'n14', type: 'parent',  status: 'unverified', notes: null },
    { linkId: 'n12-n14-cousin',sourceId: 'n12',targetId: 'n14', type: 'cousin',  status: 'unverified', notes: null },
  ],
  m2: [
    { linkId: 'n1-n2-spouse', sourceId: 'n1', targetId: 'n2', type: 'spouse', status: 'verified', verifiedBy: 'Angela Chen', verifiedAt: '2024-01-10T09:00:00Z', notes: 'Marriage certificate verified.' },
    { linkId: 'n3-n1-parent', sourceId: 'n3', targetId: 'n1', type: 'parent', status: 'verified', verifiedBy: 'Michael Chen', verifiedAt: '2024-02-15T13:00:00Z', notes: 'Family records confirm.' },
    { linkId: 'n4-n1-parent', sourceId: 'n4', targetId: 'n1', type: 'parent', status: 'verified', verifiedBy: 'Michael Chen', verifiedAt: '2024-02-15T13:00:00Z', notes: 'Family records confirm.' },
    { linkId: 'n1-n5-parent', sourceId: 'n1', targetId: 'n5', type: 'parent', status: 'unverified', notes: null },
    { linkId: 'n1-n6-parent', sourceId: 'n1', targetId: 'n6', type: 'parent', status: 'unverified', notes: null },
    { linkId: 'n1-n7-sibling',sourceId: 'n1', targetId: 'n7', type: 'sibling', status: 'pending', requestedBy: 'Thomas Chen', requestedAt: '2024-05-20T10:00:00Z', notes: 'Shared parents confirmed.' },
    { linkId: 'n1-n8-cousin', sourceId: 'n1', targetId: 'n8', type: 'cousin', status: 'unverified', notes: null },
    { linkId: 'n1-n9-cousin', sourceId: 'n1', targetId: 'n9', type: 'cousin', status: 'unverified', notes: null },
    { linkId: 'n7-n8-cousin', sourceId: 'n7', targetId: 'n8', type: 'cousin', status: 'pending', requestedBy: 'David Wei-Chen', requestedAt: '2024-04-10T14:00:00Z', notes: 'Cousin through Chen family line.' },
    { linkId: 'n9-n10-parent',sourceId: 'n9', targetId: 'n10',type: 'parent', status: 'unverified', notes: null },
    { linkId: 'n1-n10-cousin',sourceId: 'n1', targetId: 'n10',type: 'cousin', status: 'unverified', notes: null },
  ],
  m3: [
    { linkId: 'n3-n1-parent', sourceId: 'n3', targetId: 'n1', type: 'parent', status: 'verified', verifiedBy: 'Carlos Rodriguez', verifiedAt: '2024-03-01T11:00:00Z', notes: 'Birth certificate on file.' },
    { linkId: 'n4-n1-parent', sourceId: 'n4', targetId: 'n1', type: 'parent', status: 'verified', verifiedBy: 'Carlos Rodriguez', verifiedAt: '2024-03-01T11:00:00Z', notes: 'Birth certificate on file.' },
    { linkId: 'n3-n2-parent', sourceId: 'n3', targetId: 'n2', type: 'parent', status: 'verified', verifiedBy: 'Carlos Rodriguez', verifiedAt: '2024-03-01T11:00:00Z', notes: 'Birth certificate on file.' },
    { linkId: 'n4-n2-parent', sourceId: 'n4', targetId: 'n2', type: 'parent', status: 'verified', verifiedBy: 'Carlos Rodriguez', verifiedAt: '2024-03-01T11:00:00Z', notes: 'Birth certificate on file.' },
    { linkId: 'n1-n2-sibling',sourceId: 'n1', targetId: 'n2', type: 'sibling', status: 'verified', verifiedBy: 'Elena Rodriguez', verifiedAt: '2024-03-05T09:00:00Z', notes: 'Confirmed by both parents.' },
    { linkId: 'n2-n5-parent', sourceId: 'n2', targetId: 'n5', type: 'parent', status: 'unverified', notes: null },
    { linkId: 'n1-n6-cousin', sourceId: 'n1', targetId: 'n6', type: 'cousin', status: 'pending', requestedBy: 'Isabella Rodriguez-Vega', requestedAt: '2024-05-15T10:00:00Z', notes: 'Cousin through Rodriguez family.' },
    { linkId: 'n1-n7-cousin', sourceId: 'n1', targetId: 'n7', type: 'cousin', status: 'unverified', notes: null },
    { linkId: 'n2-n6-cousin', sourceId: 'n2', targetId: 'n6', type: 'cousin', status: 'unverified', notes: null },
    { linkId: 'n2-n7-cousin', sourceId: 'n2', targetId: 'n7', type: 'cousin', status: 'unverified', notes: null },
    { linkId: 'n6-n7-sibling',sourceId: 'n6', targetId: 'n7', type: 'sibling', status: 'unverified', notes: null },
    { linkId: 'n6-n8-parent', sourceId: 'n6', targetId: 'n8', type: 'parent', status: 'unverified', notes: null },
    { linkId: 'n1-n8-cousin', sourceId: 'n1', targetId: 'n8', type: 'cousin', status: 'unverified', notes: null },
  ],
};

export const getVerifications = (memorialId) =>
  DEFAULT_VERIFICATIONS[memorialId] ?? [];